import { call, put, select, take, takeLatest, all } from 'redux-saga/effects';

import { IAppState, ICommercialTrackingOfferParams, TActions } from '../../common/state';
import {
  EAnswers,
  EPaymentType,
  EUserType,
  EPageType,
  fetchSaveSurvey,
} from '../../repositories/commercial-analytics/v1/save-survey';

// TYPES
export interface ISurvey {
  answers: EAnswers[];
  paymentType?: EPaymentType;
  userType?: EUserType;
  phone?: string;
  email?: string;
  comment?: string;
}

export type TStatusFetch = 'none' | 'isLoad' | 'error' | 'ok';

// ACTIONS
export interface ICommercialSurveyWidgetModalOpened {
  type: 'modal/commercial_survey_widget/MODAL_OPENED';
  payload: {
    offerId: number;
    params: ICommercialTrackingOfferParams;
  };
}

export function openCommercialWidgetModal(
  offerId: number,
  params: ICommercialTrackingOfferParams,
): ICommercialSurveyWidgetModalOpened {
  return {
    type: 'modal/commercial_survey_widget/MODAL_OPENED',
    payload: {
      offerId,
      params,
    },
  };
}

export interface ICommercialSurveyWidgetModalClosed {
  type: 'modal/commercial_survey_widget/MODAL_CLOSED';
  payload: ISurvey | null;
}

export function closeCommercialWidgetModal(survey: ISurvey | null): ICommercialSurveyWidgetModalClosed {
  return {
    type: 'modal/commercial_survey_widget/MODAL_CLOSED',
    payload: survey,
  };
}

export interface ICommercialSurveyRequestStart {
  type: 'modal/commercial_survey_widget/REQUEST_START';
  payload: {
    statusFetch: 'isLoad';
    survey: ISurvey;
  };
}

export function commercialSurveyRequestStart(survey: ISurvey): ICommercialSurveyRequestStart {
  return {
    type: 'modal/commercial_survey_widget/REQUEST_START',
    payload: {
      statusFetch: 'isLoad',
      survey,
    },
  };
}

interface ICommercialSurveyRequestSuccess {
  type: 'modal/commercial_survey_widget/REQUEST_SUCCESS';
  payload: {
    statusFetch: 'ok';
  };
}

export function commercialSurveyRequestSuccess(): ICommercialSurveyRequestSuccess {
  return {
    type: 'modal/commercial_survey_widget/REQUEST_SUCCESS',
    payload: {
      statusFetch: 'ok',
    },
  };
}

interface ICommercialSurveyRequestError {
  type: 'modal/commercial_survey_widget/REQUEST_ERROR';
  payload: {
    statusFetch: 'error';
  };
}

export function commercialSurveyRequestError(): ICommercialSurveyRequestError {
  return {
    type: 'modal/commercial_survey_widget/REQUEST_ERROR',
    payload: {
      statusFetch: 'error',
    },
  };
}

// SAGAS
export function* awaitCommercialSurveyWidgetModalRequestStart({ payload }: ICommercialSurveyWidgetModalOpened) {
  const { httpApi } = yield select();

  const { offerId } = payload;
  const {
    payload: { survey },
  } = yield take('modal/commercial_survey_widget/REQUEST_START');

  const answer = {
    ...survey,
    offerId,
    pageType: EPageType.Listing,
  };

  try {
    const response = yield call(fetchSaveSurvey, { httpApi, parameters: answer });

    if (response.statusCode !== 200) {
      throw new Error();
    }

    yield put(commercialSurveyRequestSuccess());
  } catch (e) {
    yield put(commercialSurveyRequestError());
  }
}

export function* handleCommercialSurveyWidgetModalClosed({ payload }: ICommercialSurveyWidgetModalClosed) {
  if (!payload) {
    return;
  }

  yield put(commercialSurveyRequestStart(payload));
}

export function* commercialSurveyWidgetModalSaga() {
  yield all([
    takeLatest('modal/commercial_survey_widget/MODAL_OPENED', awaitCommercialSurveyWidgetModalRequestStart),
    takeLatest('modal/commercial_survey_widget/MODAL_CLOSED', handleCommercialSurveyWidgetModalClosed),
  ]);
}

// REDUCER
export interface ICommercialSurveyWidgetModalState {
  isCommercialSurveyWidgetModalOpen: boolean;
  commecialSurveyWidgetModalStatusFetch: TStatusFetch;
}

export type ICommercialSurveyAction =
  | ICommercialSurveyWidgetModalOpened
  | ICommercialSurveyWidgetModalClosed
  | ICommercialSurveyRequestStart
  | ICommercialSurveyRequestSuccess
  | ICommercialSurveyRequestError;

export function commercialSurveyWidgetModal(state: IAppState, action: TActions): IAppState {
  switch (action.type) {
    case 'modal/commercial_survey_widget/MODAL_OPENED':
      return {
        ...state,
        isCommercialSurveyWidgetModalOpen: true,
        commercialTrackingOfferParams: action.payload.params,
      };

    case 'modal/commercial_survey_widget/MODAL_CLOSED':
      return {
        ...state,
        isCommercialSurveyWidgetModalOpen: false,
        commercialTrackingOfferParams: undefined,
      };

    case 'modal/commercial_survey_widget/REQUEST_START':
    case 'modal/commercial_survey_widget/REQUEST_SUCCESS':
    case 'modal/commercial_survey_widget/REQUEST_ERROR':
      return {
        ...state,
        commecialSurveyWidgetModalStatusFetch: action.payload.statusFetch,
      };

    default:
      return state;
  }
}
